// Uploading media to the image-server, without a size limit and without
// anything installed on the client.
//
// The Cloudflare tunnel caps the body of a single proxied request at ~100 MB.
// That cap is per REQUEST, not per file — so a large file is sliced with
// File.slice() and sent as several requests. Plain browser APIs, works in
// Safari on a borrowed laptop, no extension and no CLI.
//
// Small files still go in one shot: a 12 MB JPEG through the chunk protocol
// would cost three round trips instead of one for no benefit.

// 64 MB leaves comfortable headroom under the ~100 MB proxy cap.
const CHUNK_SIZE = 64 * 1024 * 1024;
// Anything at or below this goes through the single-request endpoint.
const SINGLE_SHOT_MAX = 48 * 1024 * 1024;
// Concurrent chunks in flight. On a 1.2 Gb/s symmetric line this is the
// difference between saturating the link and trickling one request at a time.
const CONCURRENCY = 4;

function newUploadId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `u${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

// Derive a chunk endpoint from the grant URL, keeping its signed query string
// (path/exp/uid/sig) and swapping the path suffix. Rewriting the pathname
// rather than string-replacing keeps this correct whatever origin or base path
// the grant was issued under.
function endpoint(grantUrl, suffix, params) {
  const u = new URL(grantUrl)
  u.pathname = u.pathname.replace(/\/media\/upload(\/.*)?$/, `/media/upload${suffix}`)
  Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, String(v)))
  return u.toString()
}

async function readError(res) {
  const body = await res.json().catch(() => ({}))
  return body.error || `HTTP ${res.status}`
}

// Upload one File. `getGrantUrl()` returns a currently-valid signed grant for
// the destination folder — it is called again between chunks so a grant that
// expires mid-upload is refreshed rather than failing the file.
//
// onProgress(fraction) is called as bytes land, 0..1.
export async function uploadFile(file, getGrantUrl, onProgress) {
  const report = (f) => { try { onProgress && onProgress(f) } catch (e) { /* ignore */ } }

  if (file.size <= SINGLE_SHOT_MAX) {
    const form = new FormData()
    form.append('files', file)
    const res = await fetch(await getGrantUrl(), { method: 'POST', body: form })
    if (!res.ok) throw new Error(await readError(res))
    report(1)
    return { chunked: false }
  }

  const uploadId = newUploadId()
  const total = Math.ceil(file.size / CHUNK_SIZE)

  // Resume: ask which parts already landed. Harmless on a fresh upload (the
  // id is new, so the answer is "none") and it means a closed lid or a dropped
  // connection doesn't cost the whole file.
  let have = new Set()
  try {
    const res = await fetch(endpoint(await getGrantUrl(), '/status', { uploadId }), { method: 'GET' })
    if (res.ok) have = new Set(((await res.json()).have) || [])
  } catch (e) { /* start from scratch */ }

  let done = have.size
  report(done / total)

  const queue = []
  for (let i = 0; i < total; i++) if (!have.has(i)) queue.push(i)

  let failure = null
  async function worker() {
    while (queue.length && !failure) {
      const i = queue.shift()
      const blob = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size))
      try {
        const url = endpoint(await getGrantUrl(), '/chunk', { uploadId, index: i, total })
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: blob,
        })
        if (!res.ok) throw new Error(await readError(res))
        done++
        report(done / total)
      } catch (e) {
        failure = e
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker))
  if (failure) throw failure

  const url = endpoint(await getGrantUrl(), '/complete', { uploadId, total, name: file.name })
  const res = await fetch(url, { method: 'POST' })
  if (!res.ok) throw new Error(await readError(res))
  report(1)
  return { chunked: true, chunks: total }
}

// Ask the server to file everything sitting in _inbox/ into the library:
// hash, read capture date and camera from EXIF, deduplicate, move into place.
// `device` is only a fallback for files that carry no usable metadata.
export async function runIngest(grantUrl, device) {
  const u = new URL(grantUrl)
  u.pathname = u.pathname.replace(/\/media\/upload(\/.*)?$/, '/media/ingest')
  if (device) u.searchParams.set('device', device)
  const res = await fetch(u.toString(), { method: 'POST' })
  if (!res.ok) throw new Error(await readError(res))
  return res.json()
}

export const CHUNK_THRESHOLD = SINGLE_SHOT_MAX
export const INBOX_PATH = '_inbox'
